import React, { useState, useEffect, useMemo } from 'react';
import { X, CreditCard as Edit3, DollarSign, Target, Save, AlertCircle, Plus, Trash2, CheckSquare, Square, Search } from 'lucide-react';
import Select from 'react-select';
import { SegmentacaoConfiguracao } from '../types/multipatrocinadores';
import { multipatrocinadorService } from '../services/multipatrocinadores';

interface SegmentacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (configuracoes: SegmentacaoConfiguracao[]) => void;
  tipo: 'IDRegra' | 'BU' | 'Marca' | 'EAN';
  configuracoes: SegmentacaoConfiguracao[];
  promocaoId?: string;
  novoBudget?: number;
}

interface BUOption {
  id: string;
  nome: string;
}

interface EANOption {
  id: string;
  nome: string;
  ean: string;
  marca: string;
  industria: string;
}

const mockData = {
  IDRegra: [],
  BU: [],
  Marca: [],
  EAN: [],
};

const tiposMultipatrocinador = ['E-commerce', 'Vendedores', 'Marketplace'] as const;

const multipatrocinadorLabels: Record<string, string> = {
  'E-commerce': 'Indústria',
  'Vendedores': 'Distribuidor',
  'Marketplace': 'Marketplace'
};

export const toApiValue = (internalValue: string): string => {
  const mapping: Record<string, string> = {
    'E-commerce': 'Industria',
    'Vendedores': 'Distribuidor',
    'Marketplace': 'Marketplace'
  };
  return mapping[internalValue] || internalValue;
};

export const fromApiValue = (apiValue: string): string => {
  const mapping: Record<string, string> = {
    'Industria': 'E-commerce',
    'Distribuidor': 'Vendedores',
    'Marketplace': 'Marketplace'
  };
  return mapping[apiValue] || apiValue;
};

const getCoresMultipatrocinador = (tipo: string) => {
  switch (tipo) {
    case 'E-commerce':
      return {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        select: 'focus:ring-blue-500 focus:border-blue-500'
      };
    case 'Vendedores':
      return {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        select: 'focus:ring-green-500 focus:border-green-500'
      };
    case 'Marketplace':
      return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        select: 'focus:ring-orange-500 focus:border-orange-500'
      };
    default:
      return {
        bg: 'bg-gray-50',
        border: 'border-gray-gray-200',
        text: 'text-gray-700',
        select: 'focus:ring-gray-500 focus:border-gray-500'
      };
  }
};

export const SegmentacaoModal: React.FC<SegmentacaoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  tipo,
  configuracoes,
  promocaoId,
  novoBudget,
}) => {
  const [configuracoesInternas, setConfiguracoesInternas] = useState<SegmentacaoConfiguracao[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [buOptions, setBuOptions] = useState<BUOption[]>([]);
  const [eanOptions, setEanOptions] = useState<EANOption[]>([]);
  const [loadingBU, setLoadingBU] = useState(false);
  const [loadingEAN, setLoadingEAN] = useState(false);
  const [selectedEANs, setSelectedEANs] = useState<Set<string>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkConfig, setBulkConfig] = useState({
    coluna1: { tipo: 'E-commerce' as const, porcentagem: 0 },
    coluna2: { tipo: 'Vendedores' as const, porcentagem: 0 },
    coluna3: { tipo: 'Marketplace' as const, porcentagem: 0 },
  });
  const [searchEAN, setSearchEAN] = useState('');
  const [searchEANDebounced, setSearchEANDebounced] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const dadosDisponiveis = tipo === 'BU' ? buOptions : tipo === 'EAN' ? eanOptions : (mockData[tipo] || []);
  
  // Opções para o Select (react-select)
  const selectOptions = useMemo(() => {
    
    return dadosDisponiveis.map(item => ({
      value: item.id,
      label: item.nome
    }));
  }, [dadosDisponiveis]);

  // Debounce para o campo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchEANDebounced(searchEAN);
      setCurrentPage(1); // Reset para primeira página ao buscar
    }, 300);

    return () => clearTimeout(timer);
  }, [searchEAN]);

  // Array filtrado de configurações (memorizado para garantir re-renderização correta)
  const configuracoesFiltradasMemo = useMemo(() => {
    if (tipo !== 'EAN' || !searchEANDebounced) {
      return configuracoesInternas;
    }
    const searchLower = searchEANDebounced.toLowerCase();
    return configuracoesInternas.filter(config =>
      config.nome.toLowerCase().includes(searchLower)
    );
  }, [configuracoesInternas, searchEANDebounced, tipo]);

  // Paginação
  const totalPages = Math.ceil(configuracoesFiltradasMemo.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const configuracoesPaginadas = configuracoesFiltradasMemo.slice(startIndex, endIndex);

  // Carregar dados de BU quando necessário
  useEffect(() => {
    if (tipo === 'BU' && isOpen) {
      loadBUData();
    } else if (tipo === 'EAN' && isOpen && promocaoId) {
      loadEANData();
    }
  }, [tipo, isOpen, promocaoId]);

  const loadBUData = async () => {
    setLoadingBU(true);
    try {
      const response = await multipatrocinadorService.getBUs();
      if (response.success && response.data.success) {
        const buList = response.data.data.map((item: string[], index: number) => ({
          id: `bu_${item[0]}`,
          nome: item[0]
        }));
        setBuOptions(buList);
      }
    } catch (error) {
      console.error('Erro ao carregar BUs:', error);
    } finally {
      setLoadingBU(false);
    }
  };

  const loadEANData = async () => {
    if (!promocaoId) return;
    
    setLoadingEAN(true);
    try {
      const response = await multipatrocinadorService.getEANs(promocaoId);
      if (response.success && response.data.success) {
        const eanList = response.data.data.map((item: string[], index: number) => ({
          id: `ean_${item[0]}`, // IDSku
          nome: `${item[1]}}`, // EAN - NomeProduto
          ean: item[2], // EAN
          marca: item[3], // NomeMarca
          industria: item[4], // NomeIndustria
        }));
        setEanOptions(eanList);
      }
    } catch (error) {
      console.error('Erro ao carregar EANs:', error);
    } finally {
      setLoadingEAN(false);
    }
  };

  // Inicializar configurações quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      if (configuracoes.length > 0) {
        // Se for IDRegra, garantir que o budget seja o novo budget
        if (tipo === 'IDRegra') {
          const configAtualizada = configuracoes.map(config => ({
            ...config,
            budget: novoBudget
          }));
          setConfiguracoesInternas(configAtualizada);
        } else {
          setConfiguracoesInternas(configuracoes);
        }
      } else {
        if (tipo === 'IDRegra') {
          // Para ID Regra, criar um único registro com o ID da promoção e budget = novoBudget
          const configuracaoIDRegra: SegmentacaoConfiguracao = {
            id: promocaoId || 'idregra_default',
            nome: `ID Regra: ${promocaoId || 'N/A'}`,
            budget: novoBudget,
            multipatrocinadores: {
              coluna1: { tipo: 'E-commerce', porcentagem: 0 },
              coluna2: { tipo: 'Vendedores', porcentagem: 0 },
              coluna3: { tipo: 'Marketplace', porcentagem: 0 },
            },
          };
          setConfiguracoesInternas([configuracaoIDRegra]);
        } else if (tipo === 'Marca' || tipo === 'BU') {
          // Para marcas e BU, começar com uma linha vazia
          setConfiguracoesInternas([createNewConfiguracao()]);
        } else if (tipo === 'EAN') {
          // Para EAN, aguardar carregamento dos dados
          if (eanOptions.length > 0) {
            const novasConfiguracoes: SegmentacaoConfiguracao[] = eanOptions.map(item => ({
              id: item.id,
              nome: item.nome,
              budget: 0,
              multipatrocinadores: {
                coluna1: { tipo: 'E-commerce', porcentagem: 0 },
                coluna2: { tipo: 'Vendedores', porcentagem: 0 },
                coluna3: { tipo: 'Marketplace', porcentagem: 0 },
              },
            }));
            setConfiguracoesInternas(novasConfiguracoes);
          }
        } else {
          // Para outros tipos, usar dados pré-definidos
          const novasConfiguracoes: SegmentacaoConfiguracao[] = dadosDisponiveis.slice(0, 3).map(item => ({
            id: item.id,
            nome: item.nome,
            budget: 0,
            multipatrocinadores: {
              coluna1: { tipo: 'E-commerce', porcentagem: 0 },
              coluna2: { tipo: 'Vendedores', porcentagem: 0 },
              coluna3: { tipo: 'Marketplace', porcentagem: 0 },
            },
          }));
          setConfiguracoesInternas(novasConfiguracoes);
        }
      }
      setErrors({});
    }
  }, [isOpen, tipo, configuracoes, dadosDisponiveis, promocaoId, eanOptions, novoBudget]);

  const createNewConfiguracao = (): SegmentacaoConfiguracao => {
    const newId = `new_${Date.now()}`;
    return {
      id: newId,
      nome: '',
      budget: 0,
      multipatrocinadores: {
        coluna1: { tipo: 'E-commerce', porcentagem: 0 },
        coluna2: { tipo: 'Vendedores', porcentagem: 0 },
        coluna3: { tipo: 'Marketplace', porcentagem: 0 },
      },
    };
  };

  const handleBudgetChange = (id: string, budget: number) => {
    setConfiguracoesInternas(prev =>
      prev.map(config =>
        config.id === id ? { ...config, budget } : config
      )
    );
  };

  const handleMultipatrocinadorChange = (
    id: string,
    coluna: 'coluna1' | 'coluna2' | 'coluna3',
    field: 'tipo' | 'porcentagem',
    value: any
  ) => {
    setConfiguracoesInternas(prev =>
      prev.map(config =>
        config.id === id 
          ? {
              ...config,
              multipatrocinadores: {
                ...config.multipatrocinadores,
                [coluna]: {
                  ...config.multipatrocinadores[coluna],
                  [field]: value
                }
              }
            }
          : config
      )
    );
  };

  const toggleSelectAllComplete = () => {
    // Seleciona ou desmarca TODOS os itens cadastrados
    if (selectedEANs.size === configuracoesInternas.length && configuracoesInternas.length > 0) {
      setSelectedEANs(new Set());
    } else {
      setSelectedEANs(new Set(configuracoesInternas.map(c => c.id)));
    }
  };

  const toggleSelectVisibleOnly = () => {
    // Seleciona apenas os itens visíveis (página atual)
    const allVisibleIds = configuracoesPaginadas.map(c => c.id);
    const allVisibleSelected = allVisibleIds.every(id => selectedEANs.has(id));

    if (allVisibleSelected && configuracoesPaginadas.length > 0) {
      // Desmarcar todos os visíveis
      const newSelected = new Set(selectedEANs);
      allVisibleIds.forEach(id => newSelected.delete(id));
      setSelectedEANs(newSelected);
    } else {
      // Marcar todos os visíveis
      const newSelected = new Set(selectedEANs);
      allVisibleIds.forEach(id => newSelected.add(id));
      setSelectedEANs(newSelected);
    }
  };

  const toggleSelectEAN = (id: string) => {
    const newSelected = new Set(selectedEANs);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEANs(newSelected);
  };

  const applyBulkConfiguration = () => {
    if (selectedEANs.size === 0) return;

    // Validar se porcentagens somam 100%
    const totalPorcentagem = bulkConfig.coluna1.porcentagem + bulkConfig.coluna2.porcentagem + bulkConfig.coluna3.porcentagem;
    if (totalPorcentagem > 0 && totalPorcentagem !== 100) {
      setErrors({ bulk_error: 'As porcentagens devem somar 100%' });
      return;
    }

    setConfiguracoesInternas(prev =>
      prev.map(config =>
        selectedEANs.has(config.id)
          ? {
              ...config,
              multipatrocinadores: {
                coluna1: { ...bulkConfig.coluna1 },
                coluna2: { ...bulkConfig.coluna2 },
                coluna3: { ...bulkConfig.coluna3 },
              }
            }
          : config
      )
    );

    setShowBulkEdit(false);
    setSelectedEANs(new Set());
    setErrors({});
  };

  const handleSelectChange = (id: string, selectedOption: any) => {
    // Verificar se a BU já foi selecionada em outra linha (apenas para tipo BU)
    if (tipo === 'BU' && selectedOption) {
      const buJaSelecionada = configuracoesInternas.some(config => 
        config.id !== id && config.id === selectedOption.value
      );
      
      if (buJaSelecionada) {
        // Mostrar erro temporário
        setErrors(prev => ({
          ...prev,
          [`${id}_bu_duplicate`]: 'Esta BU já foi selecionada em outra linha'
        }));
        
        // Remover erro após 3 segundos
        setTimeout(() => {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${id}_bu_duplicate`];
            return newErrors;
          });
        }, 3000);
        
        return; // Não permitir a seleção
      }
    }

    setConfiguracoesInternas(prev =>
      prev.map(config =>
        config.id === id 
          ? { 
              ...config, 
              id: selectedOption?.value || id,
              nome: selectedOption?.label || ''
            }
          : config
      )
    );
  };

  const adicionarLinha = () => {
    const novaLinha = createNewConfiguracao();
    setConfiguracoesInternas(prev => [...prev, novaLinha]);
  };

  const removerLinha = (id: string) => {
    if (configuracoesInternas.length <= 1) return; // Manter pelo menos uma linha
    
    setConfiguracoesInternas(prev => prev.filter(config => config.id !== id));
    
    // Limpar erros da linha removida
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${id}_porcentagem`];
      delete newErrors[`${id}_bu`];
      delete newErrors[`${id}_bu_duplicate`];
      delete newErrors[`${id}_marca`];
      return newErrors;
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    configuracoesInternas.forEach(config => {
      // Validar se marca foi selecionada (para tipo Marca)
      if (tipo === 'Marca' && !config.nome) {
        newErrors[`${config.id}_marca`] = 'Selecione uma marca';
      }

      // Validar se BU foi selecionada (para tipo BU)
      if (tipo === 'BU' && !config.nome) {
        newErrors[`${config.id}_bu`] = 'Selecione uma BU';
      }

      // Validar se porcentagens somam 100% (se houver valores)
      const totalPorcentagem =
        config.multipatrocinadores.coluna1.porcentagem +
        config.multipatrocinadores.coluna2.porcentagem +
        config.multipatrocinadores.coluna3.porcentagem;

      if (totalPorcentagem > 0 && totalPorcentagem !== 100) {
        newErrors[`${config.id}_porcentagem`] = 'As porcentagens devem somar 100%';
      }
    });

    // Validar se o budget total não ultrapassa o novo budget (exceto para EAN)
    if (tipo !== 'EAN') {
      const totalBudget = getTotalBudget();
      if (totalBudget > novoBudget) {
        newErrors['budget_total'] = `O budget total (${formatCurrency(totalBudget)}) não pode ultrapassar o novo budget configurado (${formatCurrency(novoBudget)})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(configuracoesInternas);
      onClose();
    }
  };

  const getTotalBudget = () => {
    return configuracoesInternas.reduce((total, config) => total + config.budget, 0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{zIndex: 10000}}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-blue-600 text-white rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Target className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl text-white font-bold">Configurar Segmentação: {tipo}</h2>
              <p className="text-blue-100 text-sm">Configure budget e multipatrocinadores para cada item</p>
              <p className="text-blue-200 text-xs mt-1">Novo Budget Disponível: {formatCurrency(novoBudget)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Controles de seleção em massa para EAN */}
          {tipo === 'EAN' && configuracoesInternas.length > 0 && (
            <div className="mb-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleSelectAllComplete}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    {selectedEANs.size === configuracoesInternas.length && configuracoesInternas.length > 0 ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      {selectedEANs.size === configuracoesInternas.length && configuracoesInternas.length > 0 ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </span>
                    {selectedEANs.size > 0 && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                        {selectedEANs.size}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={toggleSelectVisibleOnly}
                    className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    {configuracoesPaginadas.length > 0 && configuracoesPaginadas.every(c => selectedEANs.has(c.id)) ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                    <span className="font-medium">Visíveis da Página</span>
                    {selectedEANs.size > 0 && (
                      <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                        {selectedEANs.size}
                      </span>
                    )}
                  </button>
                </div>

                {selectedEANs.size > 0 && (
                  <button
                    onClick={() => setShowBulkEdit(!showBulkEdit)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                    <span>Aplicar Configuração em Massa</span>
                  </button>
                )}
              </div>

              {/* Marcador de itens selecionados */}
              {selectedEANs.size > 0 && (
                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-full font-bold">
                      {selectedEANs.size}
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900">
                        {selectedEANs.size === 1 ? '1 item selecionado' : `${selectedEANs.size} itens selecionados`}
                      </p>
                      <p className="text-sm text-blue-700">
                        de {configuracoesInternas.length} {configuracoesInternas.length === 1 ? 'item total' : 'itens totais'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedEANs(new Set())}
                    className="text-sm text-blue-700 hover:text-blue-900 font-medium underline"
                  >
                    Limpar seleção
                  </button>
                </div>
              )}

              {/* Painel de edição em massa */}
              {showBulkEdit && selectedEANs.size > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Target className="h-5 w-5 text-green-600" />
                      <h3 className="font-bold text-gray-900">Configuração em Massa</h3>
                      <span className="text-sm text-gray-600">({selectedEANs.size} itens selecionados)</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {(['coluna1', 'coluna2', 'coluna3'] as const).map((coluna, index) => {
                      const cores = getCoresMultipatrocinador(bulkConfig[coluna].tipo);
                      return (
                        <div key={coluna} className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">Coluna {index + 1}</label>
                          <div className={`w-full px-3 py-2 border-2 rounded-lg text-sm font-bold text-center ${cores.bg} ${cores.border} ${cores.text}`}>
                            {multipatrocinadorLabels[bulkConfig[coluna].tipo]}
                          </div>
                          <div className={`relative ${cores.bg} ${cores.border} rounded`}>
                            <input
                              type="number"
                              value={bulkConfig[coluna].porcentagem}
                              onChange={(e) => setBulkConfig(prev => ({
                                ...prev,
                                [coluna]: { ...prev[coluna], porcentagem: parseFloat(e.target.value) || 0 }
                              }))}
                              className={`w-full pr-8 pl-2 py-2 border-0 rounded text-sm bg-transparent ${cores.text} focus:ring-2 ${cores.select.split(' ')[0]} focus:border-transparent`}
                              placeholder="0"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                            <span className={`absolute right-2 top-2 text-sm ${cores.text}`}>%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Total: {bulkConfig.coluna1.porcentagem + bulkConfig.coluna2.porcentagem + bulkConfig.coluna3.porcentagem}%
                      {errors.bulk_error && (
                        <span className="ml-2 text-red-600 font-medium">{errors.bulk_error}</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setShowBulkEdit(false);
                          setErrors({});
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={applyBulkConfiguration}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Save className="h-4 w-4" />
                        <span>Aplicar a {selectedEANs.size} itens</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Campo de busca para EAN */}
          {tipo === 'EAN' && configuracoesInternas.length > 0 && (
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchEAN}
                  onChange={(e) => setSearchEAN(e.target.value)}
                  placeholder="Buscar por nome do produto..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchEAN && (
                  <button
                    onClick={() => setSearchEAN('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {searchEAN && (
                <p className="mt-2 text-sm text-gray-600">
                  {configuracoesFiltradasMemo.length} resultado(s) encontrado(s)
                </p>
              )}
            </div>
          )}

          {/* Botão Adicionar (apenas para Marcas e BU) */}
          {(tipo === 'Marca' || tipo === 'BU') && (
            <div className="mb-6 flex justify-end">
              <button
                onClick={adicionarLinha}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Adicionar {tipo === 'Marca' ? 'Marca' : 'BU'}</span>
              </button>
            </div>
          )}

          {/* Loading para EAN */}
          {tipo === 'EAN' && loadingEAN && (
            <div className="mb-6 text-center">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Carregando EANs da promoção...</span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  {tipo === 'EAN' && (
                    <th className="text-center py-4 px-3 font-semibold text-gray-700 w-12">
                      <button
                        onClick={toggleSelectVisibleOnly}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={configuracoesPaginadas.length > 0 && configuracoesPaginadas.every(c => selectedEANs.has(c.id)) ? 'Desmarcar página' : 'Selecionar página'}
                      >
                        {configuracoesPaginadas.length > 0 && configuracoesPaginadas.every(c => selectedEANs.has(c.id)) ? (
                          <CheckSquare className="h-5 w-5 text-indigo-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="text-left py-4 px-3 font-semibold text-gray-700 sticky left-0 bg-white z-10 min-w-56">
                    {tipo}
                  </th>
                  {tipo !== 'EAN' && (
                    <th className="text-center py-4 px-3 font-semibold text-gray-700 min-w-32">
                      <div className="flex items-center justify-center space-x-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <span>Budget</span>
                      </div>
                    </th>
                  )}
                  <th className="text-center py-4 px-3 font-semibold text-blue-700 border-l-2 border-blue-200" colSpan={3}>
                    <div className="flex items-center justify-center space-x-2">
                      <Target className="h-4 w-4 text-blue-600" />
                      <span>Multipatrocinadores</span>
                    </div>
                  </th>
                  {(tipo === 'Marca' || tipo === 'BU') && (
                    <th className="text-center py-4 px-3 font-semibold text-gray-700 min-w-16">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {configuracoesPaginadas.map((config, index) => {
                  const hasPercentageError = errors[`${config.id}_porcentagem`];
                  const hasMarcaError = errors[`${config.id}_marca`];
                  const hasBUError = errors[`${config.id}_bu`];
                  const totalPorcentagem =
                    config.multipatrocinadores.coluna1.porcentagem +
                    config.multipatrocinadores.coluna2.porcentagem +
                    config.multipatrocinadores.coluna3.porcentagem;

                  return (
                    <tr
                      key={`${config.id}-${index}-${searchEANDebounced}-${currentPage}`}
                      className={`border-b border-gray-100 hover:bg-gray-50 ${
                        hasPercentageError || hasMarcaError || hasBUError ? 'bg-red-50' : ''
                      } ${
                        selectedEANs.has(config.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      {/* Checkbox para EAN */}
                      {tipo === 'EAN' && (
                        <td className="py-4 px-3 text-center">
                          <button
                            onClick={() => toggleSelectEAN(config.id)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            {selectedEANs.has(config.id) ? (
                              <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                      )}

                      {/* Nome/Seleção */}
                      <td className="py-4 px-3 sticky left-0 bg-white border-r border-gray-200">
                        <div className="max-w-52">
                          {tipo === 'IDRegra' ? (
                            <div className="px-2 py-1 text-sm font-medium text-gray-900 bg-blue-50 border border-blue-200 rounded-lg">
                              {config.nome}
                            </div>
                          ) : (tipo === 'Marca' || tipo === 'BU') ? (
                            <div className="space-y-1">
                              <Select
                                key={`select-${config.id}`}
                                options={selectOptions}
                                value={selectOptions.find(opt => opt.value === config.id) || null}
                                onChange={(selectedOption) => handleSelectChange(config.id, selectedOption)}
                                placeholder={`Selecione ${tipo === 'Marca' ? 'uma marca' : 'uma BU'}...`}
                                isSearchable
                                isClearable
                                isLoading={tipo === 'BU' ? loadingBU : false}
                                className="text-sm"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor: (hasMarcaError || hasBUError || errors[`${config.id}_bu_duplicate`]) ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db',
                                    '&:hover': {
                                      borderColor: (hasMarcaError || hasBUError || errors[`${config.id}_bu_duplicate`]) ? '#ef4444' : '#3b82f6'
                                    }
                                  })
                                }}
                              />
                              {(hasMarcaError || hasBUError || errors[`${config.id}_bu_duplicate`]) && (
                                <p className="text-xs text-red-600">
                                  {hasMarcaError || hasBUError || errors[`${config.id}_bu_duplicate`]}
                                </p>
                              )}
                            </div>
                          ) : tipo === 'EAN' ? (
                            <div className="px-2 py-1 text-sm font-medium text-gray-900 bg-green-50 border border-green-200 rounded-lg">
                              <div className="font-semibold">{config.nome}</div>
                              {eanOptions.find(ean => ean.id === config.id) && (
                                <div className="text-xs text-gray-600 mt-1">
                                  <div>EAN: {eanOptions.find(ean => ean.id === config.id)?.ean}</div>
                                  <div>Marca: {eanOptions.find(ean => ean.id === config.id)?.marca}</div>
                                  <div>Indústria: {eanOptions.find(ean => ean.id === config.id)?.industria}</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="px-2 py-1 text-sm font-medium text-gray-900">
                              {config.nome}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Budget */}
                      {tipo !== 'EAN' && (
                        <td className="py-4 px-3 text-center">
                          <div className="relative max-w-32 mx-auto">
                            <span className="absolute left-2 top-2 text-gray-500 text-sm">R$</span>
                            <input
                              type="number"
                              value={config.budget}
                              onChange={(e) => handleBudgetChange(config.id, parseFloat(e.target.value) || 0)}
                              disabled={tipo === 'IDRegra'}
                              className={`w-full pl-8 pr-2 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${tipo === 'IDRegra' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              placeholder="0"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </td>
                      )}

                      {/* Multipatrocinadores */}
                      {(['coluna1', 'coluna2', 'coluna3'] as const).map((coluna, colunaIndex) => (
                        <td
                          key={`${config.id}-${coluna}`}
                          className={`py-4 px-3 ${colunaIndex === 0 ? 'border-l-2 border-blue-200' : 'border-l border-blue-100'}`}
                        >
                          <div className="space-y-2">
                            {(() => {
                              const cores = getCoresMultipatrocinador(config.multipatrocinadores[coluna].tipo);
                              return (
                                <>
                                  <div className={`w-full px-3 py-2 border-2 rounded-lg text-sm font-bold text-center ${cores.bg} ${cores.border} ${cores.text}`}>
                                    {multipatrocinadorLabels[config.multipatrocinadores[coluna].tipo]}
                                  </div>

                                  <div className={`relative ${cores.bg} ${cores.border} rounded`}>
                                    <input
                                      type="number"
                                      value={config.multipatrocinadores[coluna].porcentagem}
                                      onChange={(e) => handleMultipatrocinadorChange(
                                        config.id,
                                        coluna,
                                        'porcentagem',
                                        parseFloat(e.target.value) || 0
                                      )}
                                      className={`w-full pr-8 pl-2 py-2 border-0 rounded text-sm bg-transparent ${cores.text} focus:ring-2 ${cores.select.split(' ')[0]} focus:border-transparent`}
                                      placeholder="0"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                    />
                                    <span className={`absolute right-2 top-2 text-sm ${cores.text}`}>%</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      ))}

                      {/* Ações (apenas para Marcas) */}
                      {(tipo === 'Marca' || tipo === 'BU') && (
                        <td className="py-4 px-3 text-center">
                          <button
                            onClick={() => removerLinha(config.id)}
                            disabled={configuracoesInternas.length <= 1}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remover linha"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação para EAN */}
          {tipo === 'EAN' && totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-600">
                Mostrando {startIndex + 1} a {Math.min(endIndex, configuracoesFiltradasMemo.length)} de {configuracoesFiltradasMemo.length} itens
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    // Mostrar apenas páginas próximas
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (
                      pageNum === currentPage - 2 ||
                      pageNum === currentPage + 2
                    ) {
                      return <span key={pageNum} className="px-2">...</span>;
                    }
                    return null;
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}

          {/* Errors */}
          {Object.keys(errors).length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Erros de validação:</span>
              </div>
              <ul className="mt-2 text-sm text-red-600 list-disc list-inside">
                {Object.entries(errors).map(([key, error]) => (
                  <li key={key}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              {tipo !== 'EAN' ? (
                <>
                  <div className="flex items-center space-x-4">
                    <span className={`font-medium ${getTotalBudget() > novoBudget ? 'text-red-600' : 'text-gray-600'}`}>
                      Budget Total: {formatCurrency(getTotalBudget())}
                    </span>
                    <span className="text-gray-600">
                      ({configuracoesInternas.length} itens configurados)
                    </span>
                  </div>
                  {getTotalBudget() > novoBudget && (
                    <div className="flex items-center space-x-2 mt-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-xs font-medium">
                        O budget total ultrapassa o novo budget configurado ({formatCurrency(novoBudget)})
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-gray-600">
                  {configuracoesInternas.length} itens configurados
                </span>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="h-4 w-4" />
                <span>Salvar Configurações</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};